import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchPlans } from "../store/plans/plansSlice";
import { fetchCurrentSubscription } from "../store/billing/billingSlice";

function formatPlanPrice(amountCents) {
  const cents = Number(amountCents ?? 0);
  if (Number.isNaN(cents)) return "£0";
  const amount = cents / 100;
  if (amount === 0) return "Free";
  return `£${amount.toFixed(2).replace(/\.00$/, "")}`;
}

function isCurrentPlan(subscription, plan) {
  if (!subscription || !plan) return false;

  const subscriptionPlanId =
    subscription?.plan_id ?? subscription?.plan?.id ?? subscription?.planId ?? null;
  if (subscriptionPlanId !== null && subscriptionPlanId !== undefined) {
    return String(subscriptionPlanId) === String(plan?.id);
  }

  const subscriptionPlanName =
    subscription?.plan?.name ??
    subscription?.plan?.plan_title ??
    subscription?.plan_name ??
    subscription?.plan ??
    "";

  const sp = String(subscriptionPlanName).toLowerCase();
  if (!sp) return false;

  const candidates = [plan?.id, plan?.name, plan?.plan_title]
    .filter((v) => v !== undefined && v !== null)
    .map((v) => String(v).toLowerCase());

  return candidates.some((c) => c === sp);
}

function isFreeOrTrialPlan(plan) {
  const cents = Number(plan?.amount_cents ?? 0);
  const isFree = Number.isFinite(cents) && cents === 0;
  return Boolean(plan?.is_trial) || isFree;
}

function formatDateMaybe(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString();
}

function DailyPlanCard() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const token = useAppSelector((s) => s.auth.token);
  const isLoggedIn = Boolean(token);
  const plans = useAppSelector((s) => s.plans.items);
  const plansStatus = useAppSelector((s) => s.plans.status);
  const plansError = useAppSelector((s) => s.plans.error);
  const billingSubscription = useAppSelector((s) => s.billing?.subscription);
  const billingStatus = useAppSelector((s) => s.billing?.status);
  const billingError = useAppSelector((s) => s.billing?.error);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (plansStatus === "idle" || plansStatus === "failed") {
      dispatch(fetchPlans());
    }
    dispatch(fetchCurrentSubscription({ force: true }));
  }, [isLoggedIn, plansStatus, dispatch]);

  const retryFetch = useCallback(() => {
    if (billingStatus === "loading" || plansStatus === "loading") return;
    dispatch(fetchPlans({ force: true }));
    dispatch(fetchCurrentSubscription({ force: true }));
  }, [billingStatus, plansStatus, dispatch]);

  const loadFailed = billingStatus === "failed" || plansStatus === "failed";
  const isRetrying = billingStatus === "loading" || plansStatus === "loading";

  const activePlanFromApi = useMemo(
    () =>
      Array.isArray(plans) ? plans.find((p) => p?.current_active_subscription === true) : null,
    [plans]
  );

  const subscriptionShape = useMemo(() => {
    if (billingSubscription) return billingSubscription;
    if (activePlanFromApi) {
      return {
        plan_id: activePlanFromApi.id ?? null,
        status: "active",
        cancel_at_period_end: Boolean(activePlanFromApi.cancel_at_period_end),
        current_period_end: activePlanFromApi.current_period_end ?? null,
        plan: {
          plan_id: activePlanFromApi.id ?? null,
          id: activePlanFromApi.id ?? null,
          name: activePlanFromApi.name ?? null,
          plan_title: activePlanFromApi.plan_title ?? null,
          plan_name: activePlanFromApi.name ?? null,
        },
      };
    }
    return null;
  }, [billingSubscription, activePlanFromApi]);

  const currentPlanObj = useMemo(() => {
    if (!Array.isArray(plans) || !plans.length || !subscriptionShape) return null;
    return plans.find((p) => isCurrentPlan(subscriptionShape, p)) ?? null;
  }, [plans, subscriptionShape]);

  const currentPlanName = useMemo(() => {
    return (
      currentPlanObj?.name ||
      currentPlanObj?.plan_title ||
      (typeof subscriptionShape?.plan === "string" ? subscriptionShape.plan : "") ||
      subscriptionShape?.plan?.name ||
      subscriptionShape?.plan?.plan_title ||
      "No plan selected"
    );
  }, [currentPlanObj, subscriptionShape]);

  const planSummaryLine = useMemo(() => {
    if (billingStatus === "loading") return "Loading subscription…";
    if (billingStatus === "failed") return billingError || "Failed to load subscription.";

    if (!subscriptionShape) return "No active subscription. Choose a plan to get started.";

    const currentInterval = currentPlanObj?.interval || "";
    const intervalLabel =
      currentInterval === "day"
        ? "Daily"
        : currentInterval === "month"
          ? "Monthly"
          : currentInterval
            ? String(currentInterval)
            : "";

    const left = intervalLabel
      ? `Billing ${intervalLabel}`
      : subscriptionShape?.status
        ? `Status: ${subscriptionShape.status}`
        : "No Subscription";

    const nextPaymentDate = formatDateMaybe(subscriptionShape?.current_period_end);
    const amountLabel =
      currentPlanObj && typeof currentPlanObj.amount_cents !== "undefined"
        ? formatPlanPrice(currentPlanObj.amount_cents)
        : "";

    if (subscriptionShape?.cancel_at_period_end) {
      if (nextPaymentDate) return `Cancelled | Active until ${nextPaymentDate}`;
      return "Cancelled | Will not renew";
    }

    if (isFreeOrTrialPlan(currentPlanObj)) {
      if (nextPaymentDate) return `${left} | Ends on ${nextPaymentDate}`;
      return `${left} | No renewal`;
    }

    if (nextPaymentDate && amountLabel) {
      return `Next payment on ${nextPaymentDate} for ${amountLabel}`;
    }
    if (nextPaymentDate) return `Next payment on ${nextPaymentDate}`;
    if (amountLabel) return `${amountLabel} per cycle`;
    return left;
  }, [billingStatus, billingError, subscriptionShape, currentPlanObj]);

  if (!isLoggedIn) return null;

  return (
    <div className="w-full shrink-0 rounded-xl bg-[#2FC98D1A] p-4 text-white shadow-sm md:max-w-[320px] md:self-start">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[18px] font-bold capitalize text-[#24303A]">
            {currentPlanName.replace("plan", "Plan")}
          </div>
          <div className="mt-1 text-[16px] font-normal text-[#24303A]">{planSummaryLine}</div>
          {loadFailed && (
            <button
              type="button"
              onClick={retryFetch}
              disabled={isRetrying}
              className="mt-2 rounded-full border border-red-300 bg-white px-3 py-1 text-[13px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
            >
              {isRetrying ? "Retrying…" : "Try again"}
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className="mt-3 inline-flex items-center rounded-full bg-[#7352C7] py-2 px-4 text-[16px] font-bold text-white shadow-sm hover:bg-[#6546c3]"
          >
            <span className="leading-none">Change Plan</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default DailyPlanCard;
